import { z } from 'zod';

// ============================================
// SHARED ENUMS
// ============================================

/** Access tariffs the comparator handles. 6.2TD–6.4TD are out (ERIS, 2026-10-15). */
export const ComparatorTariffSchema = z.enum(['T_20TD', 'T_30TD', 'T_61TD']).meta({
	title: 'Comparator Tariff',
	description: 'Electricity access tariff of the bill or the offer',
	example: 'T_20TD',
});

export const BILL_PERIODS = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'] as const;
export const BillPeriodSchema = z.enum(BILL_PERIODS);

/** Periods each tariff bills (spec 2.1 e): 2.0TD has two power and three energy periods. */
export const TARIFF_PERIODS: Record<
	z.infer<typeof ComparatorTariffSchema>,
	{ power: readonly (typeof BILL_PERIODS)[number][]; energy: readonly (typeof BILL_PERIODS)[number][] }
> = {
	T_20TD: { power: ['P1', 'P2'], energy: ['P1', 'P2', 'P3'] },
	T_30TD: { power: BILL_PERIODS, energy: BILL_PERIODS },
	T_61TD: { power: BILL_PERIODS, energy: BILL_PERIODS },
};

/**
 * How the bill prices energy. FLAT = one 24 h price; PERIODS = one price per
 * period; INDEXED and OTHER (e.g. flat price plus a night discount) are only
 * possible on the current side, which always uses the printed amounts.
 */
export const BillEnergyPriceTypeSchema = z.enum(['FLAT', 'PERIODS', 'INDEXED', 'OTHER']);

/** Offers are either flat 24 h or priced per period. */
export const OfferEnergyPriceTypeSchema = z.enum(['FLAT', 'PERIODS']);

/** Price segment: decided by contracted power, not by the holder (ERIS, 2026-10-15). */
export const ComparatorSegmentSchema = z.enum(['RESIDENTIAL', 'SME']);

/** Whether the offer price already includes the system adjustment services. */
export const AdjustmentServicesSchema = z.enum(['INCLUDED', 'SEPARATE']);

/**
 * Where the surplus is deducted (ERIS, 2026-10-28). ENERGY_TERM (Naturgy):
 * from the energy term down to zero, before taxes. BILL_TOTAL (Gana Energía):
 * from the bill total, after taxes. Either way, what is left over goes to the
 * virtual battery.
 */
export const SurplusCompensationSchema = z.enum(['ENERGY_TERM', 'BILL_TOTAL']);

/** Whether a maximeter reading is this bill's or the last twelve months'. */
export const MaxDemandScopeSchema = z.enum(['MONTH', 'YEAR', 'UNKNOWN']);

const money = z.number().finite();
const nonNegativeMoney = z.number().finite().nonnegative();
const periodValues = z.partialRecord(BillPeriodSchema, z.number().finite().nonnegative());

const ConceptAmountSchema = z.object({
	concept: z.string().trim().min(1).max(300),
	amount: money,
});

// ============================================
// BILL (read from the uploaded invoice and corrected by the user)
// ============================================

export const BillSurplusSchema = z.object({
	/** Every kWh poured into the grid, not only the ones compensated. */
	exportedKwh: z.number().finite().nonnegative(),
	pricePerKwh: z.number().finite().nonnegative(),
	/** Amount actually deducted on the bill, after the regulatory cap. Positive. */
	compensatedAmount: nonNegativeMoney,
	/** Value above the cap that went to the virtual battery / wallet. Positive. */
	virtualBatteryAmount: nonNegativeMoney.default(0),
	/** Deducted before taxes (energy term) or after them (bill total). */
	compensation: SurplusCompensationSchema.default('ENERGY_TERM'),
});

export const BillVatLineSchema = z.object({
	ratePct: z.number().finite().nonnegative(),
	base: money.nullable(),
	amount: money,
});

/** What the bill prints for its taxes and total: used to validate the reading. */
export const BillPrintedSchema = z.object({
	electricityTaxBase: money.nullable().default(null),
	electricityTaxRatePct: z.number().finite().nonnegative().nullable().default(null),
	electricityTaxAmount: money,
	vat: z.array(BillVatLineSchema).min(1),
	otherTaxesAmount: money.default(0),
	/** Bill total before any virtual-battery or wallet balance is applied. */
	total: money,
});

export const ComparatorBillSchema = z.object({
	tariff: ComparatorTariffSchema,
	/** Billing period. When missing, `month` stands in for the calendar check. */
	periodStart: z.iso.date().nullable().default(null),
	periodEnd: z.iso.date().nullable().default(null),
	month: z
		.string()
		.regex(/^\d{4}-(0[1-9]|1[0-2])$/)
		.nullable()
		.default(null),
	days: z.number().int().positive().max(400),
	energyPriceType: BillEnergyPriceTypeSchema,
	contractedPowerKw: periodValues,
	/** Printed power term, tolls and charges billed apart included. */
	powerAmount: money,
	/** kWh per period. Empty when the bill only prints a 24 h total. */
	consumptionKwh: periodValues,
	totalConsumptionKwh: z.number().finite().nonnegative(),
	/** Printed energy term, before regulated charges billed apart. */
	energyAmount: money,
	/** Adjustment services, efficiency fund, toll adjustments… billed outside the energy price. */
	regulatedCharges: z.array(ConceptAmountSchema).default([]),
	/** Discounts as positive amounts. They stay on the current side only. */
	discounts: z.array(ConceptAmountSchema.extend({ amount: nonNegativeMoney })).default([]),
	surplus: BillSurplusSchema.nullable().default(null),
	/** Same amount on both sides: charged by the distributor or the regulator. */
	reactiveAmount: nonNegativeMoney.default(0),
	powerExcessAmount: nonNegativeMoney.default(0),
	socialBonusAmount: nonNegativeMoney.default(0),
	meterRentalAmount: nonNegativeMoney.default(0),
	/** Maintenance, insurance and similar: removed and shown as extra savings. */
	services: z.array(ConceptAmountSchema).default([]),
	maxDemandKw: periodValues.default({}),
	maxDemandScope: MaxDemandScopeSchema.default('UNKNOWN'),
	printed: BillPrintedSchema,
});

// ============================================
// OFFER AND CONFIGURATION
// ============================================

export const ComparatorOfferSchema = z.object({
	id: z.number().int().positive(),
	name: z.string().trim().min(1).max(200),
	marketerName: z.string().trim().min(1).max(200),
	tariff: ComparatorTariffSchema,
	segment: ComparatorSegmentSchema,
	energyPriceType: OfferEnergyPriceTypeSchema,
	/** €/kW/day per period. */
	powerPricePerKwDay: periodValues,
	/** €/kWh per period (PERIODS offers). */
	energyPricePerKwh: periodValues.default({}),
	/** €/kWh for every hour (FLAT offers). */
	flatEnergyPricePerKwh: z.number().finite().nonnegative().nullable().default(null),
	/** €/kWh paid for surplus energy; null when the offer does not compensate it. */
	surplusPricePerKwh: z.number().finite().nonnegative().nullable().default(null),
	surplusCompensation: SurplusCompensationSchema.default('ENERGY_TERM'),
	adjustmentServices: AdjustmentServicesSchema,
	isActive: z.boolean().default(true),
});

export const ComparatorTaxConfigSchema = z.object({
	electricityTaxRatePct: z.number().finite().min(0).max(100),
	/** Electricity tax floor in €/MWh consumed. */
	electricityTaxMinPerMwh: z.number().finite().nonnegative(),
	vatRatePct: z.number().finite().min(0).max(100),
	/** €/kWh charged for adjustment services when an offer bills them apart (ERIS, 2026-10-28: 0.025 on average). */
	adjustmentServicesPricePerKwh: z.number().finite().nonnegative(),
});

export type ComparatorTariff = z.infer<typeof ComparatorTariffSchema>;
export type BillPeriod = z.infer<typeof BillPeriodSchema>;
export type BillPeriodValues = Partial<Record<BillPeriod, number>>;
export type BillEnergyPriceType = z.infer<typeof BillEnergyPriceTypeSchema>;
export type OfferEnergyPriceType = z.infer<typeof OfferEnergyPriceTypeSchema>;
export type ComparatorSegment = z.infer<typeof ComparatorSegmentSchema>;
export type SurplusCompensation = z.infer<typeof SurplusCompensationSchema>;
export type ComparatorBillInput = z.input<typeof ComparatorBillSchema>;
export type ComparatorBill = z.output<typeof ComparatorBillSchema>;
export type ComparatorOfferInput = z.input<typeof ComparatorOfferSchema>;
export type ComparatorOffer = z.output<typeof ComparatorOfferSchema>;
export type ComparatorTaxConfig = z.infer<typeof ComparatorTaxConfigSchema>;
